import { AnalyticsChartCard } from '@/components/ui/analytics/AnalyticsChartCard';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/shadcn/chart';
import { useChartHeightClass } from '@/hooks/use-chart-height';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { categoricalColor } from '@/lib/chart-colors';
import { settingLabel } from '@/lib/status-labels';
import { formatCurrency } from '@/lib/utils';
import type { CompaniesAnalyticsData, TopCompany } from '@/types/analytics';
import { Building2, Handshake } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';

interface Props {
    topCompanies: TopCompany[];
    companiesAnalytics?: CompaniesAnalyticsData;
}

export default function CompaniesAnalytics({ topCompanies, companiesAnalytics }: Props) {
    const { t } = useTranslations();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const isMobile = useIsMobile();
    const revenueHeightClass = isMobile ? 'h-[280px]' : 'h-[360px]';
    const segmentationHeightClass = useChartHeightClass('sm');
    const truncateCompanyTick = (v: string) => (v.length > 8 ? `${v.slice(0, 8)}…` : v);

    const kpis = useMemo(() => {
        const total = Number(companiesAnalytics?.totals?.companies ?? topCompanies.length);
        const newCompanies = Number(companiesAnalytics?.new_this_period ?? 0);
        return { total, newCompanies };
    }, [topCompanies, companiesAnalytics]);

    const topRevenue = topCompanies.slice(0, 10);
    const hasTopRevenue = topRevenue.some((c) => Number(c.revenue ?? 0) > 0);

    const safeSegmentation = useMemo(() => {
        const seg = companiesAnalytics?.companies_by_industry;
        const rows: Array<{ industry: string; count: number }> =
            Array.isArray(seg) && seg.length > 0
                ? seg
                : Object.entries(
                      topRevenue.reduce((acc: Record<string, number>, c) => {
                          const key = c.type ?? 'unknown';
                          acc[key] = (acc[key] ?? 0) + 1;
                          return acc;
                      }, {}),
                  ).map(([industry, count]) => ({ industry, count }));
        return rows.map((row) => ({ ...row, label: settingLabel(t, 'company_types', row.industry) }));
    }, [topRevenue, companiesAnalytics, t]);

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">{t('analytics.labels.page_6_companies_dashboard', 'Companies Dashboard')}</h2>
                <p className="text-muted-foreground text-sm">
                    {t('analytics.labels.account_performance', 'Account performance, concentration risk and portfolio health.')}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
                <SummaryMetricCard
                    docKey="companies.companies_total"
                    title={t('analytics.labels.companies', 'Companies')}
                    value={kpis.total}
                    subtitle={t('analytics.labels.tracked_accounts', 'Tracked accounts')}
                    icon={<Building2 className="h-4 w-4" />}
                />
                <SummaryMetricCard
                    docKey="companies.new_companies"
                    title={t('analytics.labels.new_companies', 'New Companies')}
                    value={kpis.newCompanies}
                    subtitle={t('analytics.labels.added_in_period', 'Added in period')}
                    icon={<Handshake className="h-4 w-4" />}
                />
            </div>

            {/* Interactive bar chart */}
            <AnalyticsChartCard
                docKey="companies.top_companies_by_revenue"
                title={t('analytics.labels.top_companies_by_revenue', 'Top Companies by Revenue')}
            >
                {hasTopRevenue ? (
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.top_companies_by_revenue', 'Top Companies by Revenue')}
                        config={{ revenue: { label: t('analytics.labels.revenue', 'Revenue'), color: '#2563eb' } }}
                        className={`${revenueHeightClass} w-full`}
                    >
                        <BarChart data={topRevenue} onMouseLeave={() => setActiveIndex(null)}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 11 }}
                                interval={0}
                                angle={isMobile ? -60 : -20}
                                textAnchor="end"
                                height={60}
                                tickFormatter={truncateCompanyTick}
                            />
                            <YAxis tickFormatter={(v) => formatCurrency(Number(v))} tick={{ fontSize: 11 }} width={isMobile ? 56 : 90} />
                            <ChartTooltip content={<ChartTooltipContent formatter={(v) => formatCurrency(Number(v))} />} />
                            <Bar dataKey="revenue" radius={[4, 4, 0, 0]} onMouseEnter={(_, index) => setActiveIndex(index)}>
                                {topRevenue.map((_, i) => (
                                    <Cell key={i} fill={categoricalColor(i)} opacity={activeIndex === null || activeIndex === i ? 1 : 0.45} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ChartContainer>
                ) : (
                    <div className={`text-muted-foreground flex ${revenueHeightClass} items-center justify-center text-sm`}>
                        {t('analytics.labels.no_revenue_in_period', 'No revenue in this period')}
                    </div>
                )}
            </AnalyticsChartCard>

            {/* Company segmentation */}
            {safeSegmentation.length > 0 && (
                <AnalyticsChartCard
                    docKey="companies.company_segmentation"
                    title={t('analytics.labels.company_segmentation', 'Company Segmentation')}
                >
                    <ChartContainer
                        role="img"
                        aria-label={t('analytics.labels.company_segmentation', 'Company Segmentation')}
                        config={{ count: { label: t('analytics.labels.companies', 'Companies'), color: '#ea580c' } }}
                        className={`${segmentationHeightClass} w-full`}
                    >
                        <BarChart data={safeSegmentation}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted opacity-30" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Bar dataKey="count" fill="#ea580c" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ChartContainer>
                </AnalyticsChartCard>
            )}
        </div>
    );
}
